import { useState, useEffect, useCallback, useRef } from 'react';
import { CategoryItem, CategoryType, ApiResponse } from '../types';
import { productApi } from '../services/api';

interface InfiniteScrollState {
    items: CategoryItem[];
    loading: boolean;
    error: string | null;
    hasMore: boolean;
    page: number;
    total: number;
}

interface UseInfiniteScrollOptions {
    pageSize?: number;
    type?: CategoryType | 'all';
    threshold?: number;
}

interface UseInfiniteScrollReturn extends InfiniteScrollState {
    loadMore: () => void;
    reset: () => void;
    setObserverRef: (element: HTMLElement | null) => void;
}

export const useInfiniteScroll = (
    options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollReturn => {
    const {
        pageSize = 25,
        type = 'all',
        threshold = 100
    } = options;

    const [state, setState] = useState<InfiniteScrollState>({
        items: [],
        loading: false,
        error: null,
        hasMore: true,
        page: 1,
        total: 0
    });

    const observerRef = useRef<IntersectionObserver | null>(null);
    const isLoadingRef = useRef(false);
    const typeRef = useRef(type);

    // Internal async load function
    const loadData = useCallback(async (pageToLoad: number, currentType: CategoryType | 'all') => {
        if (isLoadingRef.current) return;

        isLoadingRef.current = true;

        try {
            let response: { data: ApiResponse<CategoryItem[]> | null; error: string | null };

            if (currentType === 'all') {
                response = await productApi.getAll(pageToLoad, pageSize);
            } else {
                response = await productApi.getByType(currentType, pageToLoad, pageSize);
            }

            if (response.error) {
                setState(prev => ({
                    ...prev,
                    loading: false,
                    error: response.error
                }));
                return;
            }

            const newItems = response.data?.data || [];
            const pagination = response.data?.meta?.pagination;

            setState(prev => ({
                ...prev,
                items: pageToLoad === 1 ? newItems : [...prev.items, ...newItems],
                loading: false,
                page: pageToLoad + 1,
                total: pagination?.total || 0,
                hasMore: pagination ? pagination.page < pagination.pageCount : false
            }));


        } catch (error) {
            console.error('Load data error:', error);
            setState(prev => ({
                ...prev,
                loading: false,
                error: error instanceof Error ? error.message : 'Failed to load data'
            }));
        } finally {
            isLoadingRef.current = false;
        }
    }, [pageSize]);

    const loadMore = useCallback(() => {
        if (state.hasMore && !state.loading) {
            setState(prev => ({ ...prev, loading: true, error: null }));
            loadData(state.page, type);
        }
    }, [state.hasMore, state.loading, state.page, type, loadData]);

    const reset = useCallback((keepLoading = false) => {
        setState({
            items: [],
            loading: keepLoading,
            error: null,
            hasMore: true,
            page: 1,
            total: 0
        });
        isLoadingRef.current = false;
    }, []);

    const setObserverRef = useCallback((element: HTMLElement | null) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        if (element) {
            observerRef.current = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && state.hasMore && !isLoadingRef.current) {
                        loadMore();
                    }
                },
                {
                    rootMargin: `${threshold}px`,
                    threshold: 0.1
                }
            );

            observerRef.current.observe(element);
        }
    }, [loadMore, state.hasMore, threshold]);

    // Handle type change
    useEffect(() => {
        if (typeRef.current !== type) {
            typeRef.current = type;

            // Reset with loading state
            reset(true);

            // Load initial data after reset
            setTimeout(() => {
                loadData(1, type);
            }, 0);
        }
    }, [type, reset, loadData]);

    // Initial load only
    useEffect(() => {
        if (typeRef.current === type && state.items.length === 0 && state.page === 1 && !isLoadingRef.current) {
            setState(prev => ({ ...prev, loading: true }));
            loadData(1, type);
        }
    }, []); // Empty dependency array - only run on mount

    // Cleanup
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return {
        ...state,
        loadMore,
        reset,
        setObserverRef
    };
};